import React, { Dispatch, SetStateAction, useState } from "react"
import {
  CartContainer,
  CartItems,
  Label
} from "../../Order/ManageOrder/DeliverOrder/Cart/Cart.styled"
import Totals from "../../Order/ManageOrder/CompensationOrder/Cart/Totals/Totals"
import * as graphql from "lib/types/generated/graphql-types"
import {
  CustomReasonCode,
  Maybe,
  Order,
  OrderHistory,
  OrderOperationSearchHit
} from "lib/types/generated/graphql-types"
import Reason from "../../Order/Updates/Reason"
import { ArrowIcon, OrderLabel, OrderTotal } from "../Shared.styled"
import { Link } from "react-router-dom"
import OperationOrderLine from "./OperationOrderLine"
import OperationShippingFee from "./OperationShippingFee"
import Money from "../../Money"
import DineroFactory from "dinero.js"
import StartOperation from "./StartOperation"
import { useMutation } from "@apollo/client"
import { handleErrorMessages } from "helpers/errors"
import START_COMPENSATION from "../../../graphql/mutations/order/compensation/StartCompensation"
import FailedCompensation from "../../Order/ManageOrder/CompensationOrder/FailedCompensation"

type Props = {
  operation: OrderOperationSearchHit
  order: Order
  orderHistory: OrderHistory
  getCustomCode: (code: Maybe<string> | undefined) => CustomReasonCode
  setShow: Dispatch<SetStateAction<boolean>>
  setCreatedOperations: Dispatch<SetStateAction<OrderOperationSearchHit[]>>
  setFailedOperations: Dispatch<SetStateAction<OrderOperationSearchHit[]>>
}

const CompensationAction = ({
  operation,
  order,
  orderHistory,
  getCustomCode,
  setShow,
  setCreatedOperations,
  setFailedOperations
}: Props) => {
  const [giftCardAction, setGiftCardAction] = useState<graphql.InputActionGiftCard>({
    actionType: graphql.InputActionType.Auto
  })
  const [bonusAction, setBonusAction] = useState<graphql.InputActionBonus>({
    actionType: graphql.InputActionType.Auto
  })
  const [paymentAction, setPaymentAction] = useState<graphql.InputActionPaymentCompensation>({
    actionType: graphql.InputActionType.Auto
  })

  const compensation = order.Compensations.find((compensation) => compensation.id === operation.id)

  const [startCompensation, { loading }] = useMutation(START_COMPENSATION, {
    onCompleted: () => {
      setShow(false)
      setCreatedOperations((prev) => prev.filter((hit) => hit.id !== operation.id))
    },
    onError: (error) => {
      handleErrorMessages(error)
    }
  })

  const startOrderCompensation = () => {
    startCompensation({
      variables: {
        compensationId: compensation?.id,
        input: {
          ...(compensation?.bonus && { bonus: bonusAction }),
          ...(compensation?.giftCards &&
            compensation?.giftCards?.length > 0 && { giftCard: giftCardAction }),
          payment: paymentAction
        }
      }
    })
  }

  const getOrderLine = (compensationLineId: string) => {
    return order?.orderLines?.find((orderLine) => orderLine.id === compensationLineId)
  }

  const getShippingFee = (compensationFeeId: string) => {
    return order?.shippingFees?.find((fee) => fee.id === compensationFeeId)
  }

  const isCompensationFailed = compensation?.failed && !compensation.completed

  if (isCompensationFailed)
    return (
      <>
        <OrderLabel>
          Order: {order.reference}{" "}
          <Link to={`/orders/${order.id}`} target="_blank">
            <ArrowIcon />
          </Link>
        </OrderLabel>
        <FailedCompensation
          failedCompensation={compensation}
          orderHistory={orderHistory.history}
          setFailedOperations={setFailedOperations}
          order={order}
        />
      </>
    )

  return (
    <>
      <CartContainer>
        <OrderLabel>
          Order: {order.reference}{" "}
          <Link to={`/orders/manage-order/${order.id}`} target="_blank">
            <ArrowIcon />
          </Link>
        </OrderLabel>

        <CartItems>
          {compensation?.orderLines &&
            compensation?.orderLines.map((ol) => (
              <OperationOrderLine
                key={ol.orderLineId}
                orderLine={getOrderLine(ol.orderLineId)}
                orderQuantity={ol.quantity}
              />
            ))}
          {compensation?.shippingFees?.map((shippingFee) => (
            <OperationShippingFee
              shippingFee={getShippingFee(shippingFee.shippingFeeId)}
              key={shippingFee.shippingFeeId}
            />
          ))}
          <Reason reason={compensation?.reason} getCustomCode={getCustomCode} />
          <Label>Order:</Label>
          <OrderTotal>
            <div>Total</div>{" "}
            <div>
              <Money
                amount={order.totals.grandTotal}
                currencyUnit={order.currencyCode as DineroFactory.Currency}
              />
            </div>
          </OrderTotal>
          <Label>To be delivered: </Label>
          {compensation?.totals && (
            <Totals compensation={compensation} order={order} hideUpdateButton={true} />
          )}
        </CartItems>
        <StartOperation
          operation={compensation}
          actionState={{
            giftCardAction,
            setGiftCardAction,
            bonusAction,
            setBonusAction,
            paymentAction,
            setPaymentAction
          }}
          startOperation={startOrderCompensation}
          loading={loading}
        />
      </CartContainer>
    </>
  )
}

export default CompensationAction
